import { useState, useEffect, useCallback } from 'react'
import {
  Search, Download, Users, Mail, Phone, Trash2, X,
  Calendar, Loader2, Check, ExternalLink
} from 'lucide-react'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Badge from '../../components/ui/Badge'
import Avatar from '../../components/ui/Avatar'
import api from '../../lib/axios'

export default function Leads() {
  const [leads, setLeads] = useState([])
  const [filtered, setFiltered] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await api.get('/leads')
      setLeads(data.data || [])
    } catch (err) {
      console.error(err)
      setError('Impossible de charger les leads.')
    } finally {
      setLoading(false)
    }
  }, [])

  const applyFilters = useCallback(() => {
    let result = [...leads]

    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (l) =>
          l.name?.toLowerCase().includes(q) ||
          l.email?.toLowerCase().includes(q) ||
          l.phone?.toLowerCase().includes(q)
      )
    }

    if (sourceFilter !== 'all') {
      result = result.filter((l) => l.source === sourceFilter)
    }

    if (dateFilter !== 'all') {
      const days =
        dateFilter === '7d'
          ? 7
          : dateFilter === '30d'
            ? 30
            : 90

      const cutoff = new Date()
      cutoff.setDate(cutoff.getDate() - days)

      result = result.filter(
        (l) => new Date(l.created_at) >= cutoff
      )
    }

    setFiltered(result)
  }, [leads, search, sourceFilter, dateFilter])

  useEffect(() => {
    loadLeads()
  }, [loadLeads])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const handleExport = () => {
    const csv = [
      ['Nom', 'Email', 'Téléphone', 'Source', 'Date'],
      ...filtered.map((l) => [
        l.name || '',
        l.email || '',
        l.phone || '',
        l.source || '',
        new Date(l.created_at).toLocaleDateString('fr-FR'),
      ]),
    ]
      .map((row) =>
        row
          .map((c) => `"${String(c).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n')

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = url
    a.download = `leads-${Date.now()}.csv`
    a.click()

    URL.revokeObjectURL(url)
    setSuccess('CSV exporté !')
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleDelete = async (leadId) => {
    if (!window.confirm('Supprimer ce lead ?')) return

    try {
      await api.delete(`/leads/${leadId}`)
      setLeads((prev) => prev.filter((l) => l.id !== leadId))
      setSuccess('Lead supprimé')
      setTimeout(() => setSuccess(''), 2000)
    } catch (err) {
      console.error(err)
      setError('Erreur lors de la suppression.')
    }
  }

  const stats = [
    { label: 'Total leads', value: leads.length, icon: Users },
    {
      label: 'Cette semaine',
      value: leads.filter(
        (l) =>
          new Date(l.created_at) >=
          new Date(Date.now() - 7 * 86400000)
      ).length,
      icon: Calendar,
    },
    {
      label: 'Avec email',
      value: leads.filter((l) => l.email).length,
      icon: Mail,
    },
    {
      label: 'Avec téléphone',
      value: leads.filter((l) => l.phone).length,
      icon: Phone,
    },
  ]

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy">
            Contacts &amp; Leads
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tous les contacts capturés via vos cartes NFC et pages
          </p>
        </div>

        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="w-4 h-4" />
          Exporter CSV
        </Button>
      </div>

      {/* ALERTES */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="p-3 bg-teal-50 border border-teal-100 rounded-xl text-sm text-teal-700 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            {success}
          </span>
          <button onClick={() => setSuccess('')}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <Card key={i} className="p-5" hover>
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-teal-50 text-teal">
                <s.icon className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
            <p className="text-2xl font-bold text-navy">{s.value}</p>
          </Card>
        ))}
      </div>

      {/* FILTRES */}
      <Card className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un contact..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-graysoft rounded-xl border-0 text-sm outline-none focus:ring-2 focus:ring-teal/20"
            />
          </div>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-4 py-2.5 bg-graysoft rounded-xl border-0 text-sm font-medium text-navy outline-none focus:ring-2 focus:ring-teal/20"
          >
            <option value="all">Toutes les sources</option>
            <option value="form">Formulaire</option>
            <option value="newsletter">Newsletter</option>
            <option value="vcard">vCard</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2.5 bg-graysoft rounded-xl border-0 text-sm font-medium text-navy outline-none focus:ring-2 focus:ring-teal/20"
          >
            <option value="all">Toutes les dates</option>
            <option value="7d">7 derniers jours</option>
            <option value="30d">30 derniers jours</option>
            <option value="90d">90 derniers jours</option>
          </select>
        </div>
      </Card>

      {/* LISTE */}
      <Card className="p-6">
        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-6 h-6 text-teal animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-graysoft rounded-2xl mx-auto flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-semibold text-navy mb-2">
              {leads.length === 0
                ? 'Aucun lead pour le moment'
                : 'Aucun résultat'}
            </h3>
            <p className="text-sm text-gray-500">
              {leads.length === 0
                ? 'Les contacts capturés apparaîtront ici.'
                : 'Essayez de modifier vos filtres.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((lead) => (
              <div
                key={lead.id}
                className="flex items-center gap-4 py-4"
              >
                <Avatar name={lead.name || 'Anonyme'} size="lg" />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-navy truncate">
                      {lead.name || 'Anonyme'}
                    </p>
                    <Badge variant="success" className="text-[10px]">
                      {lead.source === 'form'
                        ? 'Formulaire'
                        : lead.source || 'Direct'}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                    {lead.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {lead.email}
                      </span>
                    )}

                    {lead.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {lead.phone}
                      </span>
                    )}

                    {lead.page && (
                      <span className="flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        {lead.page.title}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-gray-400">
                    {new Date(lead.created_at).toLocaleDateString('fr-FR')}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(lead.created_at).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>

                <button
                  onClick={() => handleDelete(lead.id)}
                  className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
